package com.gloopgo.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.viewmodel.MenuViewModel
import gloopgo.shared.generated.resources.Res
import gloopgo.shared.generated.resources.hub_subtitle
import gloopgo.shared.generated.resources.menu_tagline
import gloopgo.shared.generated.resources.hub_version_label
import gloopgo.shared.generated.resources.multiplayer
import gloopgo.shared.generated.resources.multiplayer_desc
import gloopgo.shared.generated.resources.settings
import gloopgo.shared.generated.resources.settings_desc
import gloopgo.shared.generated.resources.single_player
import gloopgo.shared.generated.resources.single_player_desc
import org.jetbrains.compose.resources.stringResource

@Composable
fun HubScreen(
    viewModel: MenuViewModel,
    onSinglePlayer: () -> Unit,
    onMultiplayer: () -> Unit,
    onSettings: () -> Unit,
) {
    LaunchedEffect(Unit) {
        viewModel.loadUserProfile()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(Color(0xFF0F172A), Color(0xFF090D16))
                )
            )
            .windowInsetsPadding(WindowInsets.safeDrawing),
        contentAlignment = Alignment.Center
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center
        ) {
            val isWide = maxWidth > 720.dp
            Column(
                modifier = Modifier
                    .widthIn(max = 880.dp)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "GLOOPGO",
                    fontSize = 40.sp,
                    fontWeight = FontWeight.Black,
                    color = Color(0xFF00E5FF),
                    letterSpacing = 2.sp
                )
                Text(
                    text = stringResource(Res.string.menu_tagline),
                    color = Color(0xFF94A3B8),
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                    letterSpacing = 0.5.sp
                )
                Text(
                    text = stringResource(Res.string.hub_subtitle),
                    color = Color(0xFF00E676),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 1.sp
                )
                Spacer(modifier = Modifier.height(8.dp))

                if (isWide) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        HubModeButton(
                            title = stringResource(Res.string.single_player),
                            description = stringResource(Res.string.single_player_desc),
                            onClick = onSinglePlayer,
                            accent = Color(0xFF00E676),
                            modifier = Modifier.weight(1f)
                        )
                        HubModeButton(
                            title = stringResource(Res.string.multiplayer),
                            description = stringResource(Res.string.multiplayer_desc),
                            onClick = onMultiplayer,
                            accent = Color(0xFF00E5FF),
                            modifier = Modifier.weight(1f)
                        )
                        HubModeButton(
                            title = stringResource(Res.string.settings),
                            description = stringResource(Res.string.settings_desc),
                            onClick = onSettings,
                            accent = Color(0xFFFFD600),
                            modifier = Modifier.weight(1f)
                        )
                    }
                } else {
                    HubModeButton(
                        title = stringResource(Res.string.single_player),
                        description = stringResource(Res.string.single_player_desc),
                        onClick = onSinglePlayer,
                        accent = Color(0xFF00E676),
                    )
                    HubModeButton(
                        title = stringResource(Res.string.multiplayer),
                        description = stringResource(Res.string.multiplayer_desc),
                        onClick = onMultiplayer,
                        accent = Color(0xFF00E5FF),
                    )
                    HubModeButton(
                        title = stringResource(Res.string.settings),
                        description = stringResource(Res.string.settings_desc),
                        onClick = onSettings,
                        accent = Color(0xFFFFD600),
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.hub_version_label, GameConstants.APP_VERSION),
                    color = Color(0xFF64748B),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 1.sp,
                    textAlign = TextAlign.Center
                )
                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}

@Composable
private fun HubModeButton(
    title: String,
    description: String,
    onClick: () -> Unit,
    accent: Color,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1E293B)),
        shape = RoundedCornerShape(16.dp),
        modifier = modifier
            .fillMaxWidth()
            .height(120.dp)
            .border(2.dp, accent, RoundedCornerShape(16.dp))
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = title,
                color = accent,
                fontSize = 18.sp,
                fontWeight = FontWeight.Black,
                letterSpacing = 1.5.sp,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = description,
                color = Color(0xFF94A3B8),
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )
        }
    }
}
